import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock3, Send, Loader2 } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal, FadeIn, Overline } from "@/components/Reveal";
import { Field, inputProps } from "@/components/Field";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const infoCards = [
  {
    icon: Phone,
    title: "Call Us",
    lines: ["(856) 545-7393", "Care line answered 24/7"],
    href: "tel:+18565457393",
    testid: "contact-phone-card",
  },
  {
    icon: Mail,
    title: "Email Us",
    lines: ["Newbeginningshealthcarenj@gmail.com", "We reply within one business day"],
    href: "mailto:Newbeginningshealthcarenj@gmail.com",
    testid: "contact-email-card",
  },
  {
    icon: MapPin,
    title: "Service Area",
    lines: ["Serving families across New Jersey", "In-home consultations available"],
    testid: "contact-area-card",
  },
  {
    icon: Clock3,
    title: "Office Hours",
    lines: ["Mon–Fri, 9am–5pm", "On-call support around the clock"],
    testid: "contact-hours-card",
  },
];

const faqs = [
  {
    q: "How quickly can care begin?",
    a: "In many cases, care can begin within 24–48 hours of your free consultation. We move at the pace your family needs — urgent situations are prioritized.",
  },
  {
    q: "Are your caregivers licensed and insured?",
    a: "Yes. New Beginnings Healthcare Services is fully licensed and insured in New Jersey. Every caregiver passes a thorough background check, reference verification, and skills screening, and works under RN supervision.",
  },
  {
    q: "Do you accept insurance or Medicaid?",
    a: "We work with many long-term care insurance plans and can help you understand your coverage options, including Medicaid programs and veteran benefits. Call us and we'll walk you through it.",
  },
  {
    q: "What if our caregiver isn't the right fit?",
    a: "Your comfort matters most. If a match doesn't feel right, we'll introduce a new caregiver promptly — at no additional cost and with no awkwardness.",
  },
  {
    q: "Is there a minimum number of hours?",
    a: "We offer flexible scheduling, from a few hours of companionship each week to 24/7 private duty nursing. Your care plan is built around your needs, not a rigid package.",
  },
];

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll be in touch within one business day.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <section data-testid="contact-hero" className="relative overflow-hidden bg-blush-50 pb-14 pt-40">
        <div className="pointer-events-none absolute -left-20 -top-20 h-64 w-64 rounded-full border border-gold/25" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn><Overline>Contact</Overline></FadeIn>
          <FadeIn delay={0.15}>
            <h1 className="mt-5 max-w-3xl font-serif text-4xl font-medium tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
              We're here to <em className="text-gold-deep">listen</em>
            </h1>
          </FadeIn>
          <FadeIn delay={0.3}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-charcoal-muted sm:text-lg">
              Questions about care, coverage, or careers? Reach out — a real
              person from our team will get back to you.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {infoCards.map((c, i) => {
              const Wrapper = c.href ? "a" : "div";
              return (
                <Reveal key={c.title} delay={i * 0.08}>
                  <Wrapper
                    {...(c.href ? { href: c.href } : {})}
                    data-testid={c.testid}
                    className="block h-full rounded-3xl border border-blush-200/80 bg-white p-7 shadow-[0_2px_20px_rgba(31,41,55,0.04)] transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-gold/40 hover:shadow-[0_16px_40px_rgba(232,180,188,0.22)]"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-gold/30 bg-gold-soft">
                      <c.icon className="h-5 w-5 text-gold" strokeWidth={1.5} />
                    </span>
                    <h2 className="mt-5 font-serif text-lg text-charcoal">{c.title}</h2>
                    <p className="mt-2 break-all text-sm font-medium text-charcoal-light">{c.lines[0]}</p>
                    <p className="mt-1 text-xs text-charcoal-faint">{c.lines[1]}</p>
                  </Wrapper>
                </Reveal>
              );
            })}
          </div>

          <div className="mt-20 grid gap-14 lg:grid-cols-[1.15fr_1fr]">
            <Reveal>
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="rounded-[2rem] border border-blush-200 bg-white p-7 shadow-[0_10px_44px_rgba(31,41,55,0.07)] sm:p-10"
              >
                <h2 className="font-serif text-2xl text-charcoal">Send us a message</h2>
                <div className="mt-8 grid gap-5 sm:grid-cols-2">
                  <Field label="Full name" htmlFor="ct-name" required>
                    <input id="ct-name" name="name" required value={form.name} onChange={update} placeholder="Your name" {...inputProps("contact-form-name-input")} />
                  </Field>
                  <Field label="Email" htmlFor="ct-email" required>
                    <input id="ct-email" name="email" type="email" required value={form.email} onChange={update} placeholder="you@email.com" {...inputProps("contact-form-email-input")} />
                  </Field>
                  <Field label="Phone (optional)" htmlFor="ct-phone">
                    <input id="ct-phone" name="phone" type="tel" value={form.phone} onChange={update} placeholder="(732) 555-0100" {...inputProps("contact-form-phone-input")} />
                  </Field>
                  <Field label="Subject" htmlFor="ct-subject" required>
                    <select id="ct-subject" name="subject" required value={form.subject} onChange={update} {...inputProps("contact-form-subject-select")}>
                      <option value="" disabled>Select a topic</option>
                      {["Care services question", "Insurance & coverage", "Referral partnership", "Careers", "Something else"].map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Message" htmlFor="ct-message" required>
                      <textarea id="ct-message" name="message" rows={5} required value={form.message} onChange={update} placeholder="How can we help?" {...inputProps("contact-form-message-textarea")} />
                    </Field>
                  </div>
                </div>
                <button
                  type="submit"
                  data-testid="contact-form-submit-button"
                  disabled={loading}
                  className="btn-pink mt-8 w-full disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {loading ? "Sending..." : "Send Message"}
                </button>
              </form>
            </Reveal>

            <div>
              <Reveal delay={0.1}>
                <Overline>Common Questions</Overline>
                <h2 className="mt-5 font-serif text-3xl font-medium tracking-tight text-charcoal sm:text-4xl">
                  Frequently asked questions
                </h2>
              </Reveal>
              <Reveal delay={0.2} className="mt-8">
                <Accordion type="single" collapsible data-testid="faq-accordion" className="space-y-3">
                  {faqs.map((f, i) => (
                    <AccordionItem
                      key={i}
                      value={`faq-${i}`}
                      className="rounded-2xl border border-blush-200 bg-white px-6 transition-colors data-[state=open]:border-gold/40"
                    >
                      <AccordionTrigger
                        data-testid={`faq-question-${i}`}
                        className="text-left font-serif text-base text-charcoal hover:text-gold-deep hover:no-underline"
                      >
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm leading-relaxed text-charcoal-muted">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
